import type { Round as PlayRound } from '../data/types';

/* One file, no network, plays the round.
 *
 * ── Why this exists ──
 * The standing commitment in CLAUDE.md, and the app's biggest structural risk: every graphic on
 * a shoot comes through one Cloudflare tunnel to one Mac Mini. Edge caching softens it, but a
 * cold image on shoot day still needs that machine to be up. This is the copy that does not.
 *
 * ── The decision that matters ──
 * It embeds THE REAL APP, not a copy of the play surface. The obvious build is a small
 * standalone HTML file with its own headline logic — that is what the original prototype was —
 * and it would start drifting from the recording surface within a month, which is the exact
 * failure this repo already warns about for the editor. Instead the exported file inlines the
 * built bundle and hands it the round through `window.__ROF_OFFLINE_ROUND__`, so it runs the
 * same PlayView, the same reveal timings and the same fake-page renderer, by construction.
 *
 * That is only possible because the play surface has no router dependency — checked, not
 * assumed: PlayView, FakePage, RealReveal, FakeReveal and useRoundProgress import nothing from
 * react-router, which is why `main.tsx` can render it outside a Router when the round is
 * embedded. If that ever stops being true this export breaks at `file://`, and the verification
 * script is what will say so.
 *
 * ── What it cannot do ──
 * A REAL headline's reveal navigates to the live article, and no amount of inlining makes the
 * internet local. The stamp still plays offline; the article afterwards needs a connection. Fake
 * pages are fully offline, which is the half that depends on the Mac Mini.
 */

const FILE_LIMIT_MB = 40;

export interface ExportProgress {
  (done: number, total: number, label: string): void;
}

/** Read the built asset URLs off the live document rather than guessing the hash. */
function assetUrls(): { js: string; css: string[] } {
  const js = document.querySelector<HTMLScriptElement>('script[type="module"][src]')?.src || '';
  const css = [...document.querySelectorAll<HTMLLinkElement>('link[rel="stylesheet"][href]')].map((l) => l.href);
  return { js, css };
}

async function asDataUri(url: string): Promise<string> {
  const res = await fetch(url);
  if (!res.ok) throw new Error(`${url} → HTTP ${res.status}`);
  const blob = await res.blob();
  return await new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(new Error(`could not read ${url}`));
    reader.readAsDataURL(blob);
  });
}

const text = async (url: string): Promise<string> => {
  const res = await fetch(url);
  if (!res.ok) throw new Error(`${url} → HTTP ${res.status}`);
  return await res.text();
};

/* Rewrites every url(...) in a stylesheet to a data URI.
 *
 * The fonts are the reason. They are self-hosted precisely so a network hiccup cannot change the
 * typeface mid-take — an export that left them as paths would reintroduce exactly that failure,
 * and at `file://` they would not resolve at all. */
async function inlineCssUrls(css: string, base: string): Promise<string> {
  const urls = [...css.matchAll(/url\(\s*['"]?([^'")]+)['"]?\s*\)/g)].map((m) => m[1]);
  const seen = new Map<string, string>();
  for (const raw of urls) {
    if (raw.startsWith('data:') || seen.has(raw)) continue;
    try {
      seen.set(raw, await asDataUri(new URL(raw, base).href));
    } catch {
      /* A missing decorative asset must not fail the whole export — the round still plays. The
         fonts are the ones that matter and their absence is visible immediately. */
      seen.set(raw, raw);
    }
  }
  return css.replace(/url\(\s*['"]?([^'")]+)['"]?\s*\)/g, (whole, raw: string) => {
    const replacement = seen.get(raw);
    return replacement ? `url("${replacement}")` : whole;
  });
}

/** Every image URL in the round, replaced by its bytes. */
async function inlineRoundImages(round: PlayRound, onProgress: ExportProgress): Promise<PlayRound> {
  const copy: PlayRound = JSON.parse(JSON.stringify(round));
  const jobs: { get: () => string; set: (v: string) => void }[] = [];

  const collect = (holder: Record<string, unknown>, key: string) => {
    const value = holder[key];
    if (typeof value === 'string' && value && !value.startsWith('data:')) {
      jobs.push({ get: () => value, set: (v) => { holder[key] = v; } });
    }
  };

  collect(copy as unknown as Record<string, unknown>, 'stampRealUrl');
  collect(copy as unknown as Record<string, unknown>, 'stampFakeUrl');
  for (const item of copy.items as unknown as Record<string, unknown>[]) {
    const images = item.images;
    if (Array.isArray(images)) {
      images.forEach((_, i) => collect(images as unknown as Record<string, unknown>, String(i)));
    }
    collect(item, 'imageUrl');
  }

  let done = 0;
  for (const job of jobs) {
    onProgress(done, jobs.length, 'artwork');
    try {
      job.set(await asDataUri(job.get()));
    } catch {
      /* Leave the original URL. The file still plays; that one graphic needs the network, and
         the caller is told how many did not make it rather than discovering it on camera. */
    }
    done += 1;
  }
  return copy;
}

export interface ExportResult {
  html: string;
  filename: string;
  bytes: number;
  /** Images that could not be inlined — they will still need the network. */
  missing: number;
}

export async function buildOfflineHtml(
  round: PlayRound,
  title: string,
  onProgress: ExportProgress = () => {},
): Promise<ExportResult> {
  const { js, css } = assetUrls();
  if (!js) throw new Error('Could not find the app bundle to embed.');

  onProgress(0, 4, 'reading the app');
  const script = await text(js);

  /* Inlined, not linked. The exported file opens from disk, where `/real-or-fake/favicon.ico`
     resolves to nothing — a linked icon would simply be missing. Non-fatal: an export without an
     icon is still an export, and this must never be the reason a round fails to download. */
  let favicon = '';
  try {
    /* Read off the link the document already declares, rather than building the path. Resolving
       'favicon-32.png' against document.baseURI gave /real-or-fake/rounds/<id>/favicon-32.png —
       relative to the CURRENT ROUTE, not the app base — so the fetch 404'd and every export
       shipped without an icon. The declared href is already correct and stays correct. */
    const declared = document.querySelector<HTMLLinkElement>('link[rel="icon"][type="image/png"]')?.href;
    if (declared) favicon = await asDataUri(declared);
  } catch { /* no icon, still a working file */ }

  onProgress(1, 4, 'styles and fonts');
  const styles: string[] = [];
  for (const href of css) styles.push(await inlineCssUrls(await text(href), href));
  /* fonts.css is a stylesheet link in index.html, so it is already in `css` above — but the
     preloads are not, and the shell's inline background rule is not either. Both are
     reproduced in the template below rather than scraped. */

  onProgress(2, 4, 'artwork');
  const embedded = await inlineRoundImages(round, onProgress);
  /* Counted from the RESULT rather than tracked through the loop: anything still pointing at
     /api/rof/img is, by definition, an image this file will need the network for. */
  const stillRemote = JSON.stringify(embedded).match(/\/api\/rof\/img\//g)?.length || 0;

  onProgress(3, 4, 'assembling');
  /* `</script>` inside the JSON would close this script tag early and produce a blank page —
     the classic way an inlined payload breaks. Escaping the angle bracket is enough and keeps
     it valid JSON. */
  const payload = JSON.stringify(embedded).replace(/</g, '\\u003c');

  const html = `<!doctype html>
<html lang="en">
<head>
<meta charset="UTF-8">
<meta name="viewport" content="width=device-width, initial-scale=1.0">
<title>${escapeHtml(title)} — Real or Fake</title>
<meta name="robots" content="noindex">
${favicon ? `<link rel="icon" href="${favicon}">` : ''}
<!-- Built by the Real or Fake builder. Everything needed to play is in this file: the app, the
     fonts and every fake page's artwork. A REAL headline's reveal still opens the live article,
     which needs a connection — nothing can inline the internet. -->
<style>
  html, body { margin: 0; height: 100%; background: #160f2b; }
  #root { height: 100%; }
</style>
<style>${styles.join('\n')}</style>
</head>
<body>
<div id="root"></div>
<script>window.__ROF_OFFLINE_ROUND__ = ${payload};</script>
<script type="module">${script}</script>
</body>
</html>`;

  const bytes = new Blob([html]).size;
  return {
    html,
    filename: `${slugForFile(title)}-offline.html`,
    bytes,
    missing: stillRemote,
  };
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c] || c));
}

function slugForFile(title: string): string {
  return title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 50) || 'round';
}

export const sizeMb = (bytes: number) => (bytes / (1024 * 1024)).toFixed(1);
export const overSizeLimit = (bytes: number) => bytes > FILE_LIMIT_MB * 1024 * 1024;
export const FILE_LIMIT = FILE_LIMIT_MB;
